import { v5 as uuidv5 } from 'uuid';
import { getConsultationDocument, materializeConsultationDocument } from '@/lib/api/consultation';
import { useVisualEditorStore } from '@/stores/visualEditorStore';
import { getAuthScopeRevision } from '@/lib/auth-scoped-state-reset';
import { getGridBlockPositions, getRecentActivityClusterOrigin } from '@/lib/utils/node-placement';
import { getWorkflowPlacementExcludedRects } from '@/lib/utils/workflow-area-snapshot';
import { addExternalNodesToCurrentCanvas } from '@/lib/utils/external-canvas-nodes';
import type { ConsultationDocument } from '@/types/consultation';
import type { CanvasNode } from '@/types/visual-editor';

/** Source identity survives upload retries, refresh and repeated archive actions. */
export function documentCanvasNodeId(projectId: string, documentId: string): string {
  return uuidv5(`uploaded-document-canvas:${projectId}:${documentId}`, uuidv5.URL);
}

export async function importDocumentsToCanvas(
  projectId: string,
  document: ConsultationDocument,
  signal?: AbortSignal,
  onProgress?: (progress: {completedItems: number; totalItems: number}) => void,
): Promise<void> {
  const revision = getAuthScopeRevision();
  const current = () => !signal?.aborted && getAuthScopeRevision() === revision
    && useVisualEditorStore.getState().projectId === projectId;
  const requireCurrent = () => {
    if (!current()) throw new DOMException('Canvas import context changed', 'AbortError');
  };
  requireCurrent();
  const sources = document.material_manifest
    ? document.material_manifest.filter(entry => entry.status === 'ready' && entry.document_id)
      .map(entry => ({id: entry.document_id!, label: `${document.filename} / ${entry.path}`}))
    : [{id: document.document_id, label: document.filename}];
  onProgress?.({completedItems: 0, totalItems: sources.length});
  // Bound metadata/materialization requests and persist each completed chunk.
  // The original archive stays intact; its manifest remains the source directory.
  for (let offset = 0; offset < sources.length; offset += 3) {
    requireCurrent();
    const nodes = await Promise.all(sources.slice(offset, offset + 3).map(async source => {
      const id = documentCanvasNodeId(projectId, source.id);
      if (useVisualEditorStore.getState().nodes.some(node => node.id === id)) return null;
      const child = source.id === document.document_id ? document
        : await getConsultationDocument(projectId, source.id, signal);
      requireCurrent();
      if (!/^(image|video)\//.test(child.mime_type ?? '')) return null;
      const attachment = await materializeConsultationDocument(projectId, source.id, signal);
      requireCurrent();
      if (!attachment.assetId || !attachment.mediaUrl || attachment.sourceDocumentId !== source.id
          || !['image', 'video'].includes(attachment.mediaType)) throw new Error('canvas_import_failed');
      const image = attachment.mediaType === 'image';
      return {
        id, assetId: attachment.assetId, mediaType: image ? 'image' : 'video',
        thumbnail: image ? attachment.mediaUrl : attachment.thumbnail,
        thumbnailUrl: image ? attachment.mediaUrl : attachment.thumbnail,
        originalImageUrl: image ? attachment.mediaUrl : undefined,
        videoUrl: image ? undefined : attachment.mediaUrl,
        duration: image ? 3 : attachment.durationSeconds ?? 0,
        ...(child.width && child.height ? {whRatio: child.width / child.height} : {}),
        sourceLabel: source.label, position: {x: 0, y: 0},
      } satisfies CanvasNode;
    }));
    requireCurrent();
    const ready = nodes.filter((node): node is NonNullable<typeof node> => node !== null);
    if (ready.length) {
      const editor = useVisualEditorStore.getState();
      // ClipNode renders landscape cards at 300px and caps preview height at
      // 360px. The generic 160px placement default would overlap these cards.
      const positions = getGridBlockPositions({
        count: ready.length,
        itemWidth: 300, itemHeight: 380,
        existingNodes: editor.nodes.map(node => ({position: node.position, width: 300, height: 380})),
        preferredOrigin: getRecentActivityClusterOrigin(editor.nodes),
        excludedRects: getWorkflowPlacementExcludedRects(editor.nodes, editor.canvasEdges),
      });
      await addExternalNodesToCurrentCanvas(ready.map((node, index) => ({...node, position: positions[index]})), {
        strictPersistence: true, focus: true, preserveProvidedPositions: true,
      });
    }
    requireCurrent();
    // A materialized asset is not yet a persisted canvas node. Publish progress
    // only after the whole chunk's strict persistence has succeeded.
    onProgress?.({completedItems: Math.min(offset + 3, sources.length), totalItems: sources.length});
  }
}
